import { useEffect, useState, useCallback } from 'react';
import { apiClient } from '@/services/api';
import { adminApiClient } from '@/services/adminApi';
import { useIsBootstrapMode, DataTable, StatusBadge } from '@connor-music/shared';
import type { Column } from '@connor-music/shared';
import type { User } from '@shared/types';
import { Users, RefreshCw, Loader2, AlertCircle } from 'lucide-react';

export default function AdminUsers() {
  const isBootstrap = useIsBootstrapMode();
  const api = isBootstrap ? adminApiClient : apiClient;
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
  const [togglingDeleteUserId, setTogglingDeleteUserId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setError('');
      const data = await api.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users.');
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleToggleDownload = async (user: User) => {
    setTogglingUserId(user.id);
    try {
      const updated = await api.toggleDownloadAccess(user.id, !user.download_allowed);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err) {
      console.error('Failed to toggle download access:', err);
      setError('Failed to update download access.');
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleToggleDeleteProjects = async (user: User) => {
    setTogglingDeleteUserId(user.id);
    try {
      const updated = await api.toggleDeleteProjectsAccess(user.id, !user.delete_projects_allowed);
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err) {
      console.error('Failed to toggle delete projects access:', err);
      setError('Failed to update delete projects access.');
    } finally {
      setTogglingDeleteUserId(null);
    }
  };

  const handleSyncUsers = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const result = await api.syncUsers();
      setSyncResult(
        `Sync complete: ${result.added} added, ${result.updated} updated, ${result.deactivated} deactivated.`
      );
      await loadUsers();
    } catch (err) {
      console.error('Failed to sync users:', err);
      setError('Failed to sync users from identity provider.');
    } finally {
      setIsSyncing(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: Column<User & Record<string, unknown>>[] = [
    {
      key: 'display_name',
      label: 'Name',
      sortable: true,
      render: (_value, item) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{item.display_name as string}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{item.email as string}</p>
        </div>
      ),
    },
    {
      key: 'is_connor_admin',
      label: 'Role',
      render: (_value, item) => (
        <StatusBadge
          status={item.is_connor_admin ? 'Admin' : item.is_music_royalty_leadership ? 'Leadership' : 'Employee'}
          type="project"
        />
      ),
    },
    {
      key: 'download_allowed',
      label: 'Download Access',
      render: (_value, item) => {
        const isToggling = togglingUserId === item.id;
        return (
          <button
            type="button"
            onClick={() => handleToggleDownload(item as unknown as User)}
            disabled={isToggling}
            className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-900"
            style={{
              backgroundColor: item.download_allowed
                ? 'rgb(var(--color-primary-600, 79 70 229))'
                : 'rgb(209 213 219)',
            }}
            role="switch"
            aria-checked={item.download_allowed as boolean}
            aria-label={`Toggle download access for ${item.display_name}`}
          >
            {isToggling ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-3 w-3 animate-spin text-white" />
              </span>
            ) : (
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  item.download_allowed ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            )}
          </button>
        );
      },
    },
    {
      key: 'delete_projects_allowed',
      label: 'Delete Projects',
      render: (_value, item) => {
        const isToggling = togglingDeleteUserId === item.id;
        return (
          <button
            type="button"
            onClick={() => handleToggleDeleteProjects(item as unknown as User)}
            disabled={isToggling}
            className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-900"
            style={{
              backgroundColor: item.delete_projects_allowed
                ? 'rgb(var(--color-primary-600, 79 70 229))'
                : 'rgb(209 213 219)',
            }}
            role="switch"
            aria-checked={item.delete_projects_allowed as boolean}
            aria-label={`Toggle delete projects access for ${item.display_name}`}
          >
            {isToggling ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-3 w-3 animate-spin text-white" />
              </span>
            ) : (
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  item.delete_projects_allowed ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            )}
          </button>
        );
      },
    },
    {
      key: 'last_login',
      label: 'Last Login',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(value as string | undefined)}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
            <Users className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Employee Users
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {users.length} {users.length === 1 ? 'user' : 'users'} registered
            </p>
          </div>
        </div>
        <button
          onClick={handleSyncUsers}
          disabled={isSyncing}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:focus:ring-offset-gray-900"
        >
          <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing...' : 'Sync from IdP'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Sync result */}
      {syncResult && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-4 text-sm text-green-700 dark:text-green-400">
          <RefreshCw className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{syncResult}</span>
        </div>
      )}

      {/* Users table */}
      <DataTable
        columns={columns}
        data={users as unknown as (User & Record<string, unknown>)[]}
        loading={isLoading}
        emptyMessage="No employees found. Sync users from your identity provider to get started."
      />
    </div>
  );
}
